/**
 * Cloudflare Turnstile, for public form links (the only case where Submit
 * creates a request, so the only case with anything to verify).
 *
 * Every trap here was found the hard way (README → Public forms):
 *   - api.js REFUSES to initialise a second time: injected again it bails out,
 *     leaves `window.turnstile` as a stub with no `render`, and still fires
 *     `load`. So it is loaded once, the guard matches by prefix (a tag carrying
 *     `?render=explicit` defeated an exact match), and the page polls for
 *     `render` because `load` can fire before the object is populated.
 *   - The host must be visible when the widget renders: Turnstile sizes its
 *     iframe from the container, and one inside `display: none` is zero-sized.
 *   - Never give an element id="turnstile": it becomes `window.turnstile` and
 *     shadows the API.
 *   - A token is single-use and lasts 300 seconds; a retry needs a reset.
 */
const API_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

let apiPromise = null;

/** Resolves once `window.turnstile.render` exists, loading api.js at most once. */
export function loadTurnstile() {
  if (window.turnstile && typeof window.turnstile.render === "function") return Promise.resolve();
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve, reject) => {
    let tries = 0;
    const waitForApi = () => {
      if (window.turnstile && typeof window.turnstile.render === "function") return resolve();
      tries += 1;
      if (tries > 100) return reject(new Error("api-loaded-but-never-initialised"));
      setTimeout(waitForApi, 100);
    };
    const existing = document.querySelector(`script[src^="${API_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", waitForApi);
      waitForApi();
      return;
    }
    const script = document.createElement("script");
    // `render=explicit` so api.js does not scan the DOM for widgets of its own.
    script.src = `${API_SRC}?render=explicit`;
    script.async = true;
    script.defer = true;
    script.onload = waitForApi;
    script.onerror = () => reject(new Error("script-blocked"));
    document.head.appendChild(script);
  });
  // A failed load may be retried on the next render.
  apiPromise.catch(() => {
    apiPromise = null;
  });
  return apiPromise;
}

export const TURNSTILE_MESSAGES = {
  failed: "Security verification failed. Please refresh the page and try again.",
  render: "Could not load the security check. Please refresh the page and try again.",
  unavailable: "Could not connect to the security verification service. Please check your connection and try again.",
};
